import { useState } from "react";
import { Megaphone, PlusCircle, Target } from "lucide-react";
import type { Role } from "../../shared/types/domain";
import { Button } from "../../shared/ui/Button";
import { Card } from "../../shared/ui/Card";
import { StatusBadge } from "../../shared/ui/Status";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import type { BusinessTarget } from "./governance.types";
import { useGovernance, useGovernanceActions } from "./useGovernance";

type TargetScope = "company" | "store";
type TargetMetric = "revenue" | "lessons" | "members" | "rating";

export function GovernanceManagementPanel({
  superView = false,
}: {
  superView?: boolean;
}) {
  const { session } = useAuth();
  const query = useGovernance();
  const organization = useOrganization();
  const actions = useGovernanceActions();
  const [targetName, setTargetName] = useState("");
  const [metric, setMetric] = useState<TargetMetric>("revenue");
  const [period, setPeriod] = useState(() =>
    new Date().toISOString().slice(0, 7),
  );
  const [value, setValue] = useState(100000);
  const [targetScope, setTargetScope] = useState<TargetScope>("store");
  const [targetStoreId, setTargetStoreId] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [audience, setAudience] = useState<Role[]>(["employee"]);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  if (!session || !query.data || !organization.data) return null;

  const stores = organization.data.stores.filter(
    (store) => store.status === "active",
  );
  const effectiveTargetScope: TargetScope = superView ? targetScope : "store";
  const effectiveTargetStoreId = superView
    ? effectiveTargetScope === "store"
      ? targetStoreId || stores[0]?.id || ""
      : null
    : session.profile.storeId;
  const announcementScope: TargetScope = superView ? "company" : "store";
  const announcementStoreId = superView ? null : session.profile.storeId;
  const storeNames = Object.fromEntries(
    organization.data.stores.map((store) => [store.id, store.name]),
  );
  const targets = query.data.targets
    .filter((target) => superView || target.storeId === session.profile.storeId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const canCreateTarget =
    targetName.trim().length > 0 &&
    period.length === 7 &&
    value > 0 &&
    (effectiveTargetScope === "company" || Boolean(effectiveTargetStoreId));

  const run = async (fn: () => Promise<unknown>, message: string) => {
    setNotice("");
    setError("");
    try {
      await fn();
      setNotice(message);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作失败");
    }
  };

  const createTarget = () =>
    run(
      () =>
        actions.createTarget({
          name: targetName,
          metric,
          period,
          targetValue: value,
          scope: effectiveTargetScope,
          storeId: effectiveTargetStoreId,
        }),
      effectiveTargetScope === "company"
        ? "全公司经营目标已创建。"
        : `${storeNames[String(effectiveTargetStoreId)] ?? "门店"}经营目标已创建。`,
    );

  return (
    <section className="governance-management">
      <div className="section-divider">
        <span>经营治理</span>
        <h2>目标与公告</h2>
      </div>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="management-grid">
        <Card className="management-card">
          <div className="card-heading">
            <Target />
            <div>
              <span>{superView ? "公司与门店目标" : "本店目标"}</span>
              <h2>创建经营目标</h2>
            </div>
          </div>
          <div className="v4-form">
            {superView && (
              <div className="form-split">
                <label className="v4-field">
                  <span>目标范围</span>
                  <select
                    value={targetScope}
                    onChange={(event) =>
                      setTargetScope(event.target.value as TargetScope)
                    }
                  >
                    <option value="store">指定门店</option>
                    <option value="company">全公司总目标</option>
                  </select>
                </label>
                <label className="v4-field">
                  <span>所属门店</span>
                  <select
                    value={String(effectiveTargetStoreId ?? "")}
                    disabled={targetScope === "company"}
                    onChange={(event) => setTargetStoreId(event.target.value)}
                  >
                    {targetScope === "company" && (
                      <option value="">全公司四店汇总</option>
                    )}
                    {stores.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            <label className="v4-field">
              <span>目标名称</span>
              <input
                value={targetName}
                onChange={(event) => setTargetName(event.target.value)}
                placeholder={
                  effectiveTargetScope === "company"
                    ? "例如：8月公司总销售目标"
                    : "例如：8月门店销售目标"
                }
              />
            </label>
            <div className="form-split">
              <label className="v4-field">
                <span>指标</span>
                <select
                  value={metric}
                  onChange={(event) =>
                    setMetric(event.target.value as TargetMetric)
                  }
                >
                  <option value="revenue">实收金额</option>
                  <option value="lessons">课消数</option>
                  <option value="members">新增会员</option>
                  <option value="rating">服务评分</option>
                </select>
              </label>
              <label className="v4-field">
                <span>月份</span>
                <input
                  type="month"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                />
              </label>
            </div>
            <label className="v4-field">
              <span>目标值</span>
              <input
                type="number"
                min="0.01"
                value={value}
                onChange={(event) => setValue(Number(event.target.value))}
              />
            </label>
            <Button disabled={!canCreateTarget || actions.pending} onClick={createTarget}>
              <PlusCircle size={15} />
              创建目标
            </Button>
          </div>
        </Card>

        <Card className="management-card">
          <div className="card-heading">
            <Megaphone />
            <div>
              <span>{superView ? "全公司发布" : "本店发布"}</span>
              <h2>发布公告</h2>
            </div>
          </div>
          <div className="v4-form">
            <label className="v4-field">
              <span>公告标题</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label className="v4-field">
              <span>公告内容</span>
              <textarea
                value={content}
                onChange={(event) => setContent(event.target.value)}
              />
            </label>
            <div className="role-checks">
              {(["member", "employee", "manager", "super"] as Role[]).map(
                (role) => (
                  <button
                    className={audience.includes(role) ? "active" : ""}
                    key={role}
                    onClick={() =>
                      setAudience(
                        audience.includes(role)
                          ? audience.filter((item) => item !== role)
                          : [...audience, role],
                      )
                    }
                  >
                    {roleLabel(role)}
                  </button>
                ),
              )}
            </div>
            <Button
              onClick={() =>
                run(
                  () =>
                    actions.publishAnnouncement({
                      title,
                      content,
                      audience,
                      scope: announcementScope,
                      storeId: announcementStoreId,
                    }),
                  "公告已发布。",
                )
              }
            >
              <Megaphone size={15} />
              立即发布
            </Button>
          </div>
        </Card>
      </div>

      <div className="target-grid">
        {targets.map((target) => (
          <TargetCard
            key={target.id}
            target={target}
            scopeLabel={targetScopeLabel(target, storeNames)}
          />
        ))}
      </div>
    </section>
  );
}

function TargetCard({
  target,
  scopeLabel,
}: {
  target: BusinessTarget;
  scopeLabel: string;
}) {
  const progress = Math.min(
    100,
    Math.round((target.actualValue / target.targetValue) * 100),
  );
  return (
    <Card className="target-card">
      <header>
        <div>
          <span>
            {target.period} · {metricLabel(target.metric)} · {scopeLabel}
          </span>
          <h3>{target.name}</h3>
        </div>
        <StatusBadge
          tone={progress >= 80 ? "success" : progress >= 50 ? "warning" : "neutral"}
        >
          {progress}%
        </StatusBadge>
      </header>
      <div className="target-progress">
        <i style={{ width: `${progress}%` }} />
      </div>
      <p>
        当前 {formatMetric(target.metric, target.actualValue)} / 目标{" "}
        {formatMetric(target.metric, target.targetValue)}
      </p>
    </Card>
  );
}

export function targetScopeLabel(
  target: Pick<BusinessTarget, "scope" | "storeId">,
  storeNames: Record<string, string>,
) {
  if (target.scope === "company") return "全公司";
  return storeNames[String(target.storeId)] ?? "指定门店";
}

function roleLabel(role: Role) {
  return { member: "会员", employee: "员工", manager: "店长", super: "超管" }[
    role
  ];
}

function metricLabel(metric: string) {
  return {
    revenue: "实收",
    lessons: "课消",
    members: "新增会员",
    rating: "评分",
  }[metric];
}

function formatMetric(metric: string, value: number) {
  return metric === "revenue"
    ? new Intl.NumberFormat("zh-CN", {
        style: "currency",
        currency: "CNY",
        maximumFractionDigits: 0,
      }).format(value)
    : metric === "rating"
      ? `${value}分`
      : `${value}`;
}
